import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { BookOpenIcon, ChevronDownIcon, ExternalLinkIcon, MailIcon, PencilIcon } from 'lucide-react'
import type { EditorPageType, EditorView } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { OrderError, TabGate, useOrder } from '@/components/app/order'
import { FormError, LoadingState, errorMessage } from '@/components/app/states'
import { Button, buttonVariants } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Spinner } from '@/components/ui/spinner'
import { usePublicConfig } from '@/components/marketing/config'

// "Edit your site": opens the full-screen page editor (/o/$orderId/edit, components/app/editor).
// HO-1: the EmDash admin stays available under "Classic editor", with its sign-in links and the
// HO-2 guide, for posts and new pages.

export const Route = createFileRoute('/o/$orderId/editor')({
  loader: ({ params, abortController }) => api<EditorView>(`/orders/${encodeURIComponent(params.orderId)}/editor`, { signal: abortController.signal }),
  pendingComponent: () => <LoadingState label="Loading" />,
  errorComponent: ({ error }) => (
    <TabGate tab="/o/$orderId/editor">
      <OrderError error={error} title="We could not load this page" />
    </TabGate>
  ),
  component: () => (
    <TabGate tab="/o/$orderId/editor">
      <EditSite />
    </TabGate>
  ),
})

function EditSite() {
  const view = Route.useLoaderData()
  const order = useOrder()
  const supportEmail = usePublicConfig().config?.supportEmail ?? null

  if (view.status === 'ready' && view.adminUrl) return <EditorLauncher view={view} adminUrl={view.adminUrl} />

  return (
    <section aria-labelledby="edit-site" className="flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 id="edit-site" className="text-xl font-semibold">
          Edit your site
        </h2>
        <p className="text-sm text-muted-foreground">Change the text and images of your pages, then publish. Visitors see your changes once you publish them.</p>
      </div>

      {view.status === 'unavailable' && (
        <p className="rounded-xl border border-border p-4 text-sm">
          The editor for {order.domain} is not set up yet.{' '}
          {supportEmail ? (
            <>
              Email us at{' '}
              <a href={`mailto:${supportEmail}?subject=${encodeURIComponent(`Editor for ${order.domain}`)}`} className="underline underline-offset-4">
                {supportEmail}
              </a>{' '}
              and we will make changes for you in the meantime.
            </>
          ) : (
            'Email us and we will make changes for you in the meantime.'
          )}
        </p>
      )}
      {view.status === 'pending' && (
        <p className="rounded-xl border border-border p-4 text-sm">
          We are setting up the editor on {order.domain}. We email you a sign-in link as soon as it is ready, usually within a few minutes.
        </p>
      )}
      {view.status !== 'unavailable' && <EditingGuide pages={view.pages} />}
    </section>
  )
}

function EditorLauncher({ view, adminUrl }: { view: EditorView; adminUrl: string }) {
  const order = useOrder()
  return (
    <section aria-labelledby="edit-site" className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-xl border border-border p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="edit-site" className="text-xl font-semibold">
            Edit your site
          </h2>
          <p className="text-sm text-muted-foreground">
            Change texts, buttons and images on the page, arrange its sections and edit the menu. Changes stay a draft until you publish them.
          </p>
        </div>
        <Link to="/o/$orderId/edit" params={{ orderId: order.id }} className={buttonVariants({ size: 'lg', className: 'shrink-0' })}>
          <PencilIcon aria-hidden />
          Open the editor
        </Link>
      </div>

      <Collapsible className="rounded-xl border border-border">
        <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
          Classic editor: blog posts and new pages
          <ChevronDownIcon className="size-4 transition-transform group-data-[panel-open]:rotate-180" aria-hidden />
        </CollapsibleTrigger>
        <CollapsibleContent className="flex max-w-2xl flex-col gap-6 border-t border-border px-4 py-4">
          <Ready adminUrl={adminUrl} email={view.email} />
          <EditingGuide pages={view.pages} />
        </CollapsibleContent>
      </Collapsible>
    </section>
  )
}

function Ready({ adminUrl, email }: { adminUrl: string; email: string | null }) {
  const { id } = useOrder()
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)

  const send = async () => {
    setState('sending')
    setError(null)
    try {
      await api<{ sent: true }>(`/orders/${encodeURIComponent(id)}/editor/sign-in-link`, { method: 'POST' })
      setState('sent')
    } catch (e) {
      setError(errorMessage(e))
      setState('idle')
    }
  }

  return (
    <>
      <div className="flex flex-col gap-3 rounded-xl border border-border p-4">
        <p className="text-sm">
          Editor address: <span className="font-mono break-all">{adminUrl}</span>
        </p>
        <a href={adminUrl} target="_blank" rel="noopener noreferrer" className={buttonVariants({ className: 'w-fit' })}>
          Open the editor
          <ExternalLinkIcon aria-hidden />
        </a>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="flex items-center gap-2 font-medium">
          <MailIcon className="size-4" aria-hidden />
          Signing in
        </h3>
        <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
          <li>The first time, open the sign-in link we emailed{email ? ` to ${email}` : ''}. It works once and expires after 7 days.</li>
          <li>Next time, open the editor address and enter your email address. We email you a sign-in link. It expires after 15 minutes.</li>
        </ol>
        <p className="text-sm text-muted-foreground">
          You do not need a password.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Button variant="outline" className="w-fit" onClick={send} disabled={state === 'sending'}>
          {state === 'sending' ? <Spinner /> : <MailIcon aria-hidden />}
          Email me a new sign-in link
        </Button>
        <p role="status" className="text-sm text-muted-foreground">
          {state === 'sent' ? `Sent${email ? ` to ${email}` : ''}. The link works once and expires after 7 days.` : ''}
        </p>
        <FormError message={error} />
      </div>
    </>
  )
}

const WHERE: Record<EditorPageType['where'], string> = {
  pages: 'Pages',
  posts: 'Posts',
  auto: 'Updates by itself',
}

function EditingGuide({ pages }: { pages: EditorPageType[] }) {
  const hasPosts = pages.some((p) => p.where === 'posts')
  return (
    <div className="flex flex-col gap-4 border-t border-border pt-6">
      <h3 className="flex items-center gap-2 font-medium">
        <BookOpenIcon className="size-4" aria-hidden />
        Editing guide
      </h3>

      {pages.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm">Where to find each part of your site in the editor:</p>
          <table className="w-full text-left text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-1 pr-4 font-normal">Page type</th>
                <th className="py-1 pr-4 font-normal">Pages</th>
                <th className="py-1 font-normal">In the editor</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.name} className="border-t border-border">
                  <td className="py-1.5 pr-4">{p.name}</td>
                  <td className="py-1.5 pr-4 tabular-nums">{p.count}</td>
                  <td className="py-1.5">{WHERE[p.where]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm text-muted-foreground">Lists of posts, such as category and archive pages, update by themselves when you add or edit a post.</p>
        </div>
      )}

      <dl className="flex flex-col gap-3 text-sm">
        <div>
          <dt className="font-medium">Change text on a page</dt>
          <dd className="text-muted-foreground">
            Open <b>Pages</b> and choose the page. Each part of the page is a section. In an <b>Original section</b>, open
            <b> Content</b> and change the <b>Text</b> of an element. A <b>Text section</b> works like a normal text editor.
          </dd>
        </div>
        <div>
          <dt className="font-medium">Replace an image</dt>
          <dd className="text-muted-foreground">
            In the section, find the element of type <b>image</b>, choose a new <b>Image</b>, and update its
            <b> Image description</b>. The description is read aloud to people who cannot see the image, and search engines use it.
          </dd>
        </div>
        <div>
          <dt className="font-medium">Change a link or button</dt>
          <dd className="text-muted-foreground">
            Change the <b>Text</b> and the <b>Link</b> of the element of type <b>link</b>. Keep links to your own pages short, for example
            <code className="mx-1 rounded bg-muted px-1">/contact/</code>.
          </dd>
        </div>
        <div>
          <dt className="font-medium">Add text to a page</dt>
          <dd className="text-muted-foreground">Add a <b>Text section</b> where you want it. Headings, lists, links and images work there.</dd>
        </div>
        {hasPosts && (
          <div>
            <dt className="font-medium">Write a blog post</dt>
            <dd className="text-muted-foreground">
              Open <b>Posts</b> and add a new post. Give it a title, a short excerpt and a featured image, then write the content.
            </dd>
          </div>
        )}
        <div>
          <dt className="font-medium">Header, footer and menus</dt>
          <dd className="text-muted-foreground">
            Open <b>Header and footer</b> to change the text there, and <b>Menus</b> to add, rename or reorder menu links.
          </dd>
        </div>
        <div>
          <dt className="font-medium">Publish</dt>
          <dd className="text-muted-foreground">
            Save keeps a draft. <b>Publish</b> puts the change on your site, and visitors see it within a minute.
          </dd>
        </div>
        <div>
          <dt className="font-medium">Fields to leave as they are</dt>
          <dd className="text-muted-foreground">
            Fields marked <b>set by Hedgehog, leave as is</b> keep your design in place. If you change them, the section can lose its layout.
            If that happens, email us and we put it back.
          </dd>
        </div>
      </dl>
    </div>
  )
}
